import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Loader,
  Message,
  Modal,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

const readModels = (channel) => ({
  id: channel.id,
  name: channel.name,
  status: channel.status,
  models: Array.isArray(channel.models)
    ? channel.models
    : (channel.models || '')
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean),
  disabled_models: channel.disabled_models || [],
});

export default function ChannelModelsModal({
  channel,
  onClose,
  onUpdated,
  onTest,
}) {
  const [current, setCurrent] = useState(() => readModels(channel));
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    API.get(`/api/channel/${channel.id}`)
      .then(({ data }) => {
        if (!active) return;
        if (!data.success) throw new Error(data.message);
        setCurrent(readModels(data.data));
      })
      .catch((err) => {
        if (active) setError(err.message || '加载模型配置失败');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [channel.id]);

  const toggle = async (name, enabled) => {
    setBusy(name);
    try {
      const { data } = await API.put(
        `/api/channel/${channel.id}/model-status`,
        { model: name, enabled }
      );
      if (!data.success) throw new Error(data.message);
      setCurrent((previous) => readModels({ ...previous, ...data.data }));
      onUpdated(data.data);
      showSuccess(`${name} 已${enabled ? '启用' : '停用'}`);
    } catch (err) {
      showError(err.message || '更新模型开关失败');
    } finally {
      setBusy('');
    }
  };

  const test = async (name) => {
    setBusy(name);
    try {
      await onTest(name);
    } catch (err) {
      showError(err.message || '模型测试失败');
    } finally {
      setBusy('');
    }
  };

  const disabled = new Set(current.disabled_models);
  const visible = current.models.filter((name) =>
    name.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <Modal
      open
      size='small'
      onClose={busy ? undefined : onClose}
      closeOnDimmerClick={!busy}
      closeOnEscape={!busy}
    >
      <Modal.Header>
        模型管理 · {current.name || `渠道 #${current.id}`}
      </Modal.Header>
      <Modal.Content scrolling>
        <Message info>
          停用后该模型不再接收新的业务请求，模型配置会保留，已提交的任务继续处理。
        </Message>
        {current.status !== 1 && (
          <Message warning>
            当前渠道整体已停用；开启模型后，仍需启用渠道才能接收请求。
          </Message>
        )}
        {error && <Message negative>{error}</Message>}
        {loading ? (
          <Loader active inline='centered' />
        ) : (
          !error && (
            <>
              <Input
                fluid
                icon='search'
                placeholder='搜索模型'
                value={search}
                onChange={(e, { value }) => setSearch(value)}
              />
              <p style={{ marginTop: 12 }}>
                共 {current.models.length} 个，模型开关开启{' '}
                {current.models.length - disabled.size} 个，关闭 {disabled.size}{' '}
                个
              </p>
              <Table basic='very' compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>模型</Table.HeaderCell>
                    <Table.HeaderCell>模型开关</Table.HeaderCell>
                    {onTest && <Table.HeaderCell>测试</Table.HeaderCell>}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {visible.map((name) => (
                    <Table.Row key={name}>
                      <Table.Cell style={{ overflowWrap: 'anywhere' }}>
                        {name}
                      </Table.Cell>
                      <Table.Cell>
                        <Checkbox
                          toggle
                          aria-label={`${name} 模型开关`}
                          checked={!disabled.has(name)}
                          disabled={Boolean(busy)}
                          onChange={(e, { checked }) => toggle(name, checked)}
                        />
                        <Label
                          basic
                          size='tiny'
                          color={disabled.has(name) ? 'grey' : 'green'}
                        >
                          {disabled.has(name) ? '已停用' : '已启用'}
                        </Label>
                      </Table.Cell>
                      {onTest && (
                        <Table.Cell>
                          <Button
                            size='tiny'
                            disabled={Boolean(busy)}
                            loading={busy === name}
                            onClick={() => test(name)}
                          >
                            测试
                          </Button>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                  {!visible.length && (
                    <Table.Row>
                      <Table.Cell colSpan={onTest ? 3 : 2}>
                        暂无匹配的模型
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </>
          )
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button disabled={Boolean(busy)} onClick={onClose}>
          关闭
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
